import {
  ICarbon,
  IClimateChangeStructure,
  IGhGasEmissionVerify,
  IGreenhouse,
  IGreenhouseGasOther,
  IGreenhouseGoal,
  IGreenhouseStandard,
  INetZero,
  IPerformanceGhGasMgmt,
} from '@Domains/Esg/EsgGreenhouse';
import { IOtherDescription } from '@Domains/utils';
import { mapAssets } from '@Utils/assetUtils';
import dayjs from 'dayjs';
import { CommitteeType, GoalType } from 'src/Enum/EsgGreenhouse';
import {
  ICarbonFormValues,
  IClimateChangeStructureFormValues,
  ICommitteeTypeFormValues,
  IGhGasEmissionVerifyFormValues,
  IGoalTypeFormValues,
  IGreenhouseFormValues,
  IGreenhouseGasOtherFormValues,
  IGreenhouseGoalFormValues,
  IGreenhouseStandardFormValues,
  INetZeroFormValues,
  IPerformanceGhGasMgmtFormValues,
} from './Containers/EsgGreenhouseFormProvider/types';

export const mapEsgGreenhouseToFormValues = (
  greenhouse: IGreenhouse,
  formValues?: IGreenhouseFormValues
) => {
  const {
    greenhouseStandard,
    greenhouseGoalTerm,
    climateChangeStructure,
    performanceGhGasMgmtReport,
    otherDescriptions,
    ...values
  } = greenhouse;

  return {
    ...values,
    greenhouseStandard:
      greenhouseStandard &&
      mapGhStandard(greenhouseStandard, formValues?.greenhouseStandard),
    greenhouseGoalTerm: {
      ...greenhouseGoalTerm,
      greenhouseGoal: mapGreenhouseGoalDataToForm(
        greenhouseGoalTerm?.greenhouseGoal
      ),
      netZero: mapNetZeroDataToForm(greenhouseGoalTerm?.netZero),
      carbon: mapCarbonDataToForm(greenhouseGoalTerm?.carbon),
      other: mapGreenhouseOtherDataToForm(greenhouseGoalTerm?.other),
    },
    climateChangeStructure:
      climateChangeStructure &&
      mapClimateChangeStructureToFormValue(climateChangeStructure),
    performanceGhGasMgmtReport: {
      ...performanceGhGasMgmtReport,
      ghGasEmissionVerify: mapGhGasEmissionVerifyDataToForm(
        performanceGhGasMgmtReport?.ghGasEmissionVerify
      ),
      performanceGhGasMgmt: mapPerformanceGhGasMgmtToFormValue(
        performanceGhGasMgmtReport?.performanceGhGasMgmt
      ),
    },
    otherDescriptions: mapOtherDescriptions(otherDescriptions),
    isAllCompletedEn: false,
    isAllCompletedTh: false,
  } as unknown as IGreenhouseFormValues;
};

const mapGhStandard = (
  standard: IGreenhouseStandard,
  standardFormValues?: IGreenhouseStandardFormValues
) => {
  const { principles, ...values } = standard;

  const principleFormatted = principles?.reduce(
    (acc, principle) => ({
      ...acc,
      [`principle${principle.infoCategoryMasterDataId}`]: {
        id: principle.id,
        infoCategoryMasterDataId: principle.infoCategoryMasterDataId,
        nameTh: principle?.nameTh,
        nameEn: principle?.nameEn,
        checked: true,
      },
    }),
    {}
  ) as IGreenhouseStandardFormValues['principles'];

  return {
    ...values,
    principles: { ...standardFormValues?.principles, ...principleFormatted },
  };
};

const mapGreenhouseGoalDataToForm = (
  greenhouseGoal?: IGreenhouseGoal
): IGreenhouseGoalFormValues => {
  if (!greenhouseGoal) {
    return {
      goals: { netZero: false, carbon: false, other: false },
    };
  }

  const { goals = [], remark, ...values } = greenhouseGoal;
  const mapGoalType: IGoalTypeFormValues = {
    netZero: goals.includes(GoalType.NET_ZERO),
    carbon: goals.includes(GoalType.CARBON),
    other: goals.includes(GoalType.OTHER),
  };

  return {
    ...values,
    remark,
    goals: mapGoalType,
  } as IGreenhouseGoalFormValues;
};

const mapNetZeroDataToForm = (netZero?: INetZero): INetZeroFormValues => {
  const { details, ...values } = netZero || {};

  return {
    ...values,
    details: details?.map((detail) => {
      const { baseYear, nearTermTarget, longTermTarget } = detail;

      return {
        ...detail,
        baseYear: {
          ...baseYear,
          year: dayjs(baseYear.year),
        },
        nearTermTarget: {
          ...nearTermTarget,
          year: nearTermTarget?.year ? dayjs(nearTermTarget.year) : undefined,
        },
        longTermTarget: {
          ...longTermTarget,
          year: longTermTarget?.year ? dayjs(longTermTarget.year) : undefined,
        },
      };
    }),
  } as INetZeroFormValues;
};
const mapCarbonDataToForm = (carbon?: ICarbon) => {
  const { remark, details, ...values } = carbon || {};

  return {
    ...values,
    remark,
    details: details?.map((detail) => {
      const { baseYear, target } = detail;
      return {
        ...detail,
        baseYear: {
          ...baseYear,
          year: baseYear?.year ? dayjs(baseYear.year) : undefined,
        },
        target: {
          ...target,
          year: dayjs(target?.year) ?? undefined,
        },
      };
    }),
  } as ICarbonFormValues;
};

const mapGreenhouseOtherDataToForm = (ghOther?: IGreenhouseGasOther) => {
  if (!ghOther) return undefined;

  const { details, remark, ...values } = ghOther;
  return {
    ...values,
    remark,
    details: details.map((detail) => {
      const { scopeId, baseYear, nearTermTarget, longTermTarget } = detail;

      return {
        ...detail,
        scope: { infoCategoryMasterDataId: scopeId },
        baseYear: {
          ...baseYear,
          year: baseYear?.year ? dayjs(baseYear.year) : undefined,
        },
        nearTermTarget: {
          ...nearTermTarget,
          year: nearTermTarget?.year ? dayjs(nearTermTarget.year) : undefined,
        },
        longTermTarget: {
          ...longTermTarget,
          year: longTermTarget?.year ? dayjs(longTermTarget.year) : undefined,
        },
      };
    }),
  } as IGreenhouseGasOtherFormValues;
};

const mapGhGasEmissionVerifyDataToForm = (
  ghGasEmissionVerify?: IGhGasEmissionVerify
): IGhGasEmissionVerifyFormValues => {
  const { verifier, ...values } = ghGasEmissionVerify ?? {};
  return {
    ...values,
    verifier: verifier
      ? {
          ...verifier,
          otherNameTh: verifier?.nameTh ?? undefined,
          otherNameEn: verifier?.nameEn ?? undefined,
        }
      : { infoCategoryMasterDataId: '' },
  } as IGhGasEmissionVerifyFormValues;
};

const mapCommitteeToFormValue = (
  committee: CommitteeType[]
): ICommitteeTypeFormValues => {
  const mapCommitteeType: ICommitteeTypeFormValues = {
    boardResponsibility: committee?.includes(CommitteeType.BOARD_OF_DIRECTOR),
    subcommitteeResponsibility: committee?.includes(CommitteeType.SUBCOMMITTEE),
  };

  return mapCommitteeType as ICommitteeTypeFormValues;
};

const mapClimateChangeStructureToFormValue = (
  climateChangeStructure?: IClimateChangeStructure
): IClimateChangeStructureFormValues => {
  if (!climateChangeStructure) return {} as IClimateChangeStructureFormValues;

  const { committee = [], ...values } = climateChangeStructure;

  return {
    ...values,
    committee: mapCommitteeToFormValue(committee),
  } as IClimateChangeStructureFormValues;
};

const mapOtherDescriptions = (
  descriptions: IOtherDescription[] | null | undefined
) => {
  return Array.isArray(descriptions)
    ? descriptions.map((desc) => ({
        id: desc?.id,
        titleTh: desc?.titleTh ?? null,
        titleEn: desc?.titleEn ?? null,
        descriptionTh: desc?.descriptionTh ?? null,
        descriptionEn: desc?.descriptionEn ?? null,
      }))
    : [];
};

const mapPerformanceGhGasMgmtToFormValue = (
  performanceGhGasMgmt?: IPerformanceGhGasMgmt
): IPerformanceGhGasMgmtFormValues => {
  if (!performanceGhGasMgmt) return {} as IPerformanceGhGasMgmtFormValues;
  const { assets, ...values } = performanceGhGasMgmt;
  return {
    ...values,
    assets: mapAssets(assets),
  } as IPerformanceGhGasMgmtFormValues;
};
